// Import the API functions from journeyApi with different names to avoid conflicts
import { 
  apiStartEnhancedChat as journeyApiStartChat, 
  apiSendEnhancedChatMessage as journeyApiSendMessage, 
  apiGetEnhancedChatHistory as journeyApiGetHistory, 
  apiRegenerateEnhancedChatEmbeddings as journeyApiRegenerateEmbeddings, 
  apiSubmitEnhancedChatFeedback as journeyApiSubmitFeedback 
} from './journeyApi';

/**
 * Enhanced Chat API utilities for the new chatbot system
 */

export class EnhancedChatApi {
  /**
   * Start a new enhanced chat conversation
   * @param {Object} params - Parameters for starting conversation
   * @param {string} params.journey_uuid - UUID of the journey
   * @param {string} [params.visitor_email] - Optional visitor email
   * @param {Object} [params.user_context] - Optional user context data
   * @returns {Promise<Object>} Conversation data with greeting and context
   */
  async startConversation({ journey_uuid, visitor_email, user_context = {} }) {
    try {
      const response = await journeyApiStartChat(journey_uuid, visitor_email, user_context);
      
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Error starting enhanced chat conversation:', error);
      return {
        success: false,
        error: error.message || 'Failed to start conversation',
      };
    }
  }


  async sendMessage({ journey_uuid, message, visitor_email, user_context = {}, stream = false }) {
        try {
          const response = await journeyApiSendMessage(journey_uuid, message, visitor_email, user_context, stream);      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Error sending enhanced chat message:', error);
      return {
        success: false,
        error: error.message || 'Failed to send message',
      };
    }
  }

  /**
   * Get conversation history
   * @param {string} conversation_id - ID of the conversation
   * @param {number} [limit] - Maximum number of messages to retrieve
   * @returns {Promise<Object>} Conversation history
   */
  async getConversationHistory(conversation_id, limit = 50) {
    try {
      const response = await journeyApiGetHistory(conversation_id, limit);
      
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Error getting conversation history:', error);
      return {
        success: false,
        error: error.message || 'Failed to get conversation history',
      };
    }
  }

  /**
   * Regenerate embeddings for a conversation
   * @param {string} conversation_id - ID of the conversation
   * @returns {Promise<Object>} Result of regeneration
   */
  async regenerateEmbeddings(conversation_id) {
    try {
      const response = await journeyApiRegenerateEmbeddings(conversation_id);
      
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Error regenerating embeddings:', error);
      return {
        success: false,
        error: error.message || 'Failed to regenerate embeddings',
      };
    }
  }

  /**
   * Submit feedback for a message
   * @param {Object} params - Feedback parameters
   * @param {string} params.message_id - ID of the message
   * @param {string} params.feedback_type - Type of feedback ('thumbs_up', 'thumbs_down')
   * @param {string} [params.comment] - Optional feedback comment
   * @returns {Promise<Object>} Result of feedback submission
   */
  async submitMessageFeedback({ message_id, feedback_type, comment }) {
    try {
      const response = await journeyApiSubmitFeedback(message_id, feedback_type, comment);
      
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Error submitting message feedback:', error);
      return {
        success: false,
        error: error.message || 'Failed to submit feedback',
      };
    }
  }
}

// Create a singleton instance
export const enhancedChatApi = new EnhancedChatApi();

// Legacy function exports for backward compatibility
export const apiStartEnhancedChat = enhancedChatApi.startConversation.bind(enhancedChatApi);
export const apiSendEnhancedChatMessage = enhancedChatApi.sendMessage.bind(enhancedChatApi);
export const apiGetEnhancedChatHistory = enhancedChatApi.getConversationHistory.bind(enhancedChatApi);
export const apiRegenerateEnhancedChatEmbeddings = enhancedChatApi.regenerateEmbeddings.bind(enhancedChatApi);
export const apiSubmitEnhancedChatFeedback = enhancedChatApi.submitMessageFeedback.bind(enhancedChatApi); 